import { AnyFunc } from './typeHelper';
import { isShallowEqual } from './utils';

export function memoize<Args extends any[], Ret>(
  fn: AnyFunc<Args, Ret>,
  opts: memoize.Options<Args, Ret> = {},
): (...args: Args) => Ret {
  const cacheMap = opts.cache ?? new memoize.DefaultCacheMap<Args, Ret>();

  return (...args) => {
    if (!cacheMap.has(args)) {
      cacheMap.set(args, fn(...args));
    }
    return cacheMap.get(args)!;
  };
}

export namespace memoize {
  export interface CacheMap<K, V> {
    get(key: K): undefined | V;
    set(key: K, value: V): void;
    has(key: K): boolean;
    delete(key: K): void;
  }

  export interface Options<CacheKey, CacheValue> {
    cache?: CacheMap<CacheKey, CacheValue>;
  }

  export class DefaultCacheMap<K, V> implements CacheMap<K, V> {
    private map = new Map<K, V>();

    private getStoredKey(key: K): undefined | K;
    private getStoredKey(key: K, fallbackKey: K): K;
    private getStoredKey(key: K, fallbackKey?: K): undefined | K {
      let storedKey: undefined | K;

      for (const [k] of this.map) {
        if (isShallowEqual(key, k)) {
          storedKey = k;
          break;
        }
      }

      return storedKey ?? fallbackKey;
    }

    get(key: K): undefined | V {
      const storedKey = this.getStoredKey(key);
      return storedKey != null ? this.map.get(storedKey) : undefined;
    }

    set(key: K, value: V): void {
      this.map.set(this.getStoredKey(key, key), value);
    }

    has(key: K): boolean {
      return !!this.getStoredKey(key);
    }

    delete(key: K): void {
      this.map.delete(this.getStoredKey(key, key));
    }
  }
}
